import React, { useState } from "react";
import axios from "axios";
import config from "../../config";
import { getStrategistQuestions, getStrategistStrategy } from "../../actions/aiAction";
import "./StrategistChat.css";

const serverConfig = { withCredentials: true };

/**
 * Conversational Strategist overlay.
 *
 * Flow:
 *   questions → strategy → (user gives feedback → questions again → strategy ...) → commit
 *
 * Props:
 *  - nodeId:    Post._id
 *  - onClose:   () => void
 *  - onApplied: () => void
 */
const StrategistChat = ({ nodeId, onClose, onApplied }) => {
  // phase: "loading_questions" | "questions" | "loading_strategy" | "result" | "error"
  const [phase, setPhase] = useState("loading_questions");
  const [error, setError] = useState(null);

  // Questions phase
  const [opening, setOpening] = useState("");
  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({}); // { qId: "option" | null }
  const [freeText, setFreeText] = useState("");               // single free-text box per round

  // Result phase
  const [reasoning, setReasoning] = useState("");
  const [actions, setActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState(new Set()); // indices user agrees with
  const [feedback, setFeedback] = useState("");                       // "want something different?"

  // Conversation history — passed to backend so each round has context
  const [rounds, setRounds] = useState([]);

  const [applying, setApplying] = useState(false);

  React.useEffect(() => { fetchQuestions([]); }, []);

  const buildCurrentAnswers = () => {
    const answers = questions.map((q) => {
      const selected = selectedOptions[q.id] || null;
      const parts = [];
      if (selected) parts.push(selected);
      if (freeText.trim()) parts.push(`Additional context: ${freeText.trim()}`);
      return { question: q.question, answer: parts.join(". ") || "(no specific preference)" };
    });
    if (questions.length === 0 && freeText.trim()) {
      answers.push({ question: "Additional context from user", answer: freeText.trim() });
    }
    return answers;
  };

  const fetchQuestions = async (history) => {
    setPhase("loading_questions");
    setError(null);
    setSelectedOptions({});
    setFreeText("");
    try {
      const data = await getStrategistQuestions(nodeId, history);
      setOpening(data.opening || "");
      setQuestions(data.questions || []);
      setPhase("questions");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load questions. Please try again.");
      setPhase("error");
    }
  };

  // Called when user clicks "Strategise" — asks LLM if it has enough context first.
  const evaluateAndProceed = async () => {
    const currentAnswers = buildCurrentAnswers();
    setPhase("loading_strategy");
    setError(null);
    try {
      const evalData = await getStrategistQuestions(nodeId, rounds, currentAnswers);
      if (evalData.readyToStrategise) {
        const data = await getStrategistStrategy(nodeId, currentAnswers, rounds);
        setReasoning(data.reasoning || "");
        setActions(data.actions || []);
        setSelectedActions(new Set(data.actions.map((_, i) => i)));
        setFeedback("");
        setPhase("result");
      } else {
        setOpening(evalData.opening || "");
        setQuestions(evalData.questions || []);
        setSelectedOptions({});
        setFreeText("");
        setPhase("questions");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate strategy. Please try again.");
      setPhase("error");
    }
  };

  const handleRefine = () => {
    const newRound = {
      questionsAndAnswers: questions.map((q) => ({
        question: q.question,
        answer: selectedOptions[q.id] || "(skipped)",
      })),
      freeText,
      strategy: { reasoning, actions },
      acceptedActions: actions.filter((_, i) => selectedActions.has(i)),
      feedback,
    };
    const newHistory = [...rounds, newRound];
    setRounds(newHistory);
    fetchQuestions(newHistory);
  };

  const handleApply = async () => {
    if (applying) return;
    const committedActions = actions.filter((_, i) => selectedActions.has(i));
    if (committedActions.length === 0) return;
    setApplying(true);
    try {
      const payload = JSON.stringify({ reasoning, actions: committedActions });
      await axios.post(
        `${config.apiUrl}/api/v1/addupdate`,
        { postId: nodeId, updateNote: `[Dmyor Assist] ${payload}` },
        serverConfig
      );
      if (onApplied) onApplied();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to log actions. Please try again.");
      setApplying(false);
    }
  };

  const toggleAction = (i) => {
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleOption = (qId, opt) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [qId]: prev[qId] === opt ? null : opt, // click same option = deselect
    }));
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="st-panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="st-header">
          <span className="st-title">
            <i className="bx bx-target-lock" />
            The Strategist
            {rounds.length > 0 && (
              <span className="st-round-badge">Round {rounds.length + 1}</span>
            )}
          </span>
          <button className="st-close" onClick={onClose}>&times;</button>
        </div>

        {/* Loading */}
        {(phase === "loading_questions" || phase === "loading_strategy") && (
          <div className="st-loading">
            <div className="st-bar-track"><div className="st-bar-fill" /></div>
            <span className="st-loading-text">
              {phase === "loading_questions" ? "Reading your journey..." : "Mapping your next moves..."}
            </span>
          </div>
        )}

        {/* Error */}
        {phase === "error" && (
          <div className="st-error">
            <p className="st-error-text">{error}</p>
            <button className="st-btn st-btn-primary" onClick={() => fetchQuestions(rounds)}>
              Try Again
            </button>
          </div>
        )}

        {/* Questions */}
        {phase === "questions" && (
          <div className="st-questions-wrap">
            {opening && (
              <div className="st-opening"><p>{opening}</p></div>
            )}

            <div className="st-questions-list">
              {questions.map((q, qi) => (
                <div key={q.id} className="st-question">
                  <p className="st-question-text">
                    <span className="st-question-num">{qi + 1}.</span> {q.question}
                  </p>
                  {q.type === "mcq" && q.options && (
                    <div className="st-options">
                      {q.options.map((opt) => (
                        <button
                          key={opt}
                          className={`st-option ${selectedOptions[q.id] === opt ? "selected" : ""}`}
                          onClick={() => toggleOption(q.id, opt)}
                        >
                          {selectedOptions[q.id] === opt && <i className="bx bx-check st-opt-check" />}
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="st-freetext-wrap">
              <label className="st-freetext-label">Anything to add or clarify?</label>
              <textarea
                className="st-text-answer"
                placeholder="Your thoughts, constraints, preferences..."
                rows={3}
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
              />
            </div>

            <div className="st-footer-actions">
              <button className="st-btn st-btn-primary" onClick={evaluateAndProceed}>
                Strategise
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {phase === "result" && (
          <div className="st-result-wrap">
            {reasoning && (
              <div className="st-reasoning">
                <p className="st-reasoning-label">Why this strategy</p>
                <p className="st-reasoning-text">{reasoning}</p>
              </div>
            )}

            <p className="st-actions-hint">Select the actions you agree with:</p>
            <div className="st-actions-list">
              {actions.map((action, i) => {
                const checked = selectedActions.has(i);
                return (
                  <div
                    key={i}
                    className={`st-action-item ${checked ? "selected" : ""}`}
                    onClick={() => !applying && toggleAction(i)}
                  >
                    <div className="st-action-check">
                      {checked && <i className="bx bx-check" />}
                    </div>
                    <span className="st-action-text">{action}</span>
                  </div>
                );
              })}
            </div>

            <div className="st-freetext-wrap">
              <label className="st-freetext-label">Not quite right? What would you change?</label>
              <textarea
                className="st-text-answer"
                placeholder="Tell the Strategist what's missing or what you'd prefer..."
                rows={3}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={applying}
              />
            </div>

            {applying && <p className="st-applying">Logging actions...</p>}

            <div className="st-footer-actions">
              <button
                className="st-btn st-btn-secondary"
                onClick={handleRefine}
                disabled={applying}
              >
                Refine
              </button>
              <button
                className="st-btn st-btn-primary"
                disabled={selectedActions.size === 0 || applying}
                onClick={handleApply}
              >
                Commit {selectedActions.size > 0 ? `(${selectedActions.size})` : ""}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StrategistChat;
